import api from "./api";

/**
 * @file tenant.js
 * @description Servicio HTTP del tablero de Plataforma. Pega contra los
 *              endpoints `/tenants/*` del backend, que solo aceptan a
 *              usuarios `platform-admin`. Cualquier otro tipo de user recibe
 *              403 — el frontend no debería mostrar la UI que llama estos
 *              métodos a no-platform-admins (el getter `isPlatformAdmin` del
 *              store es el guard de UI).
 */

const tenantService = {};
const endpoint = () => `/tenants`;

/**
 * @description Atajo: trae la Organization de la DB actual sin tener que
 *              conocer su `_id`. La usa el dashboard al entrar al tablero.
 * @returns {Promise<Object>} Tenant (Organization serializada)
 */
tenantService.getCurrent = function () {
  return api.get(`${endpoint()}/current`).then((res) => res.data);
};

/**
 * @description Lista de tenants visibles en esta DB. En modelo "una DB por
 *              tenant" devuelve un array de 1 elemento. Mantenido como lista
 *              por compatibilidad futura con modelos central (B/C).
 * @returns {Promise<{ docs: Object[], total: number }>}
 */
tenantService.list = function () {
  return api.get(endpoint()).then((res) => res.data);
};

/**
 * @description Devuelve un tenant por su `_id`.
 * @param {string} id
 * @returns {Promise<Object>}
 */
tenantService.getById = function (id) {
  return api.get(`${endpoint()}/${id}`).then((res) => res.data);
};

/**
 * @description Edita campos de identidad (name, key, kind, domain,
 *              internalNotes, timezone, status, preset, email).
 * @param {string} id
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
tenantService.updateIdentity = function (id, payload) {
  return api.put(`${endpoint()}/${id}`, payload).then((res) => res.data);
};

/**
 * @description Edita las feature flags del tenant. `features` es un objeto
 *              `{ [featureKey]: boolean }` que reemplaza al actual entero
 *              (no es delta).
 * @param {string} id
 * @param {Object} features
 * @returns {Promise<Object>}
 */
tenantService.updateFeatures = function (id, features) {
  return api
    .put(`${endpoint()}/${id}/features`, { features })
    .then((res) => res.data);
};

/**
 * @description Edita el branding del tenant (logo, colores, tipografía, etc).
 * @param {string} id
 * @param {Object} branding
 * @returns {Promise<Object>}
 */
tenantService.updateBranding = function (id, branding) {
  return api
    .put(`${endpoint()}/${id}/branding`, { branding })
    .then((res) => res.data);
};

/**
 * @description UC-REG-067. Lee el toggle "compartir pacientes entre médicos"
 *              (FILTER_ALL_PATIENT) del org.
 * @param {string} id
 * @returns {Promise<{ sharePatientsAcrossDoctors: boolean }>}
 */
tenantService.getSharing = function (id) {
  return api.get(`${endpoint()}/${id}/sharing`).then((res) => res.data);
};

/**
 * @description UC-REG-067. Toggle del modo "policonsultorio compartido".
 *              Reemplaza el insert manual de Parametrization via mongoshell.
 * @param {string} id
 * @param {boolean} sharePatientsAcrossDoctors
 * @returns {Promise<{ sharePatientsAcrossDoctors: boolean }>}
 */
tenantService.updateSharing = function (id, sharePatientsAcrossDoctors) {
  return api
    .put(`${endpoint()}/${id}/sharing`, { sharePatientsAcrossDoctors })
    .then((res) => res.data);
};

/**
 * @description UC-REG-068. Edita notifications de la org. emailFrom necesita
 *              que el dominio esté verificado en SES del tenant — si no, los
 *              envíos van a fallar y caen al sender default del tenant
 *              (fallback in-flight con warning en log).
 * @param {string} id
 * @param {Object} notifications - { emailFrom?, emailFromName?,
 *                                   patientAssignmentEnabled? }
 * @returns {Promise<{ notifications: Object }>}
 */
tenantService.updateNotifications = function (id, notifications) {
  return api
    .put(`${endpoint()}/${id}/notifications`, notifications)
    .then((res) => res.data);
};

export default tenantService;
